// src/services/ethicsCodeService.js
import { fetchWithAuth } from "../utils/fetchWithAuth";

const BASE = "/ethics-code";

/* Utils */
const enc = (v) => encodeURIComponent(String(v ?? ""));
const jsonOrThrow = async (res, label = "") => {
  let data = null;
  try { data = await res.json(); } catch {}
  if (!res.ok) {
    const text =
      data?.message ||
      data?.error ||
      data?.detail ||
      (await res.text().catch(() => ""));
    const err = new Error(`${label || ""} HTTP ${res.status}${text ? ` ${text}` : ""}`.trim());
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
};
const pick = (obj, keys) =>
  Object.fromEntries(
    Object.entries(obj || {}).filter(([k, v]) => keys.includes(k) && v !== undefined)
  );

const isValidCtaUrl = (s) => {
  if (!s) return true;
  if (s.startsWith("mailto:")) return s.slice(7).includes("@");
  try {
    const u = new URL(s);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

/* ================== LIST ================== */
/** Lista de documentos. Puedes pasar { publishedOnly: true }. */
export const listEthicsDocs = async (opts = {}) => {
  const qs = opts.publishedOnly ? "?publishedOnly=true" : "";
  const res = await fetchWithAuth(`${BASE}${qs}`, { method: "GET" });
  const data = await jsonOrThrow(res, `GET ${BASE}`);
  return Array.isArray(data) ? data : [];
};

/* ================== UPLOAD ================== */
/** Sube un PDF (campo multipart "file"). Backend: POST /ethics-code/upload */
export const uploadEthicsDoc = async ({
  file, title, versionTag, isPublished = true,
} = {}) => {
  if (!file) throw new Error("Archivo requerido");
  const fd = new FormData();
  fd.append("file", file, file.name || "codigo-etica.pdf");
  if (title != null) fd.append("title", String(title).trim());
  if (versionTag != null) fd.append("versionTag", String(versionTag).trim());
  fd.append("isPublished", String(!!isPublished));

  const res = await fetchWithAuth(`${BASE}/upload`, { method: "POST", body: fd });
  return jsonOrThrow(res, `POST ${BASE}/upload`);
};

/* ================== DOWNLOAD URL ================== */
export const downloadEthicsDocUrl = (id) => (id ? `${BASE}/${enc(id)}/download` : "#");

/* ================== UPDATE DOC ================== */
export const updateEthicsDoc = async (id, payload = {}) => {
  if (!id) throw new Error("ID requerido");
  const body = {};
  if (payload.title != null) body.title = String(payload.title).trim();
  if (payload.versionTag != null) body.versionTag = String(payload.versionTag).trim();
  if (typeof payload.isPublished === "boolean") body.isPublished = !!payload.isPublished;

  const res = await fetchWithAuth(`${BASE}/${enc(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res, `PATCH ${BASE}/${id}`);
};

/* ================== DELETE DOC ================== */
export const deleteEthicsDoc = async (id) => {
  const res = await fetchWithAuth(`${BASE}/${enc(id)}`, { method: "DELETE" });
  await jsonOrThrow(res, `DELETE ${BASE}/${id}`);
  return true;
};

/* ================== LATEST ================== */
export const getLatestEthicsDoc = async () => {
  const res = await fetchWithAuth(`${BASE}/latest`, { method: "GET" });
  return jsonOrThrow(res, `GET ${BASE}/latest`);
};

/* ================== CONFIG (página) ================== */
export const getEthicsConfig = async () => {
  const res = await fetchWithAuth(`${BASE}/config`, { method: "GET" });
  const cfg = await jsonOrThrow(res, `GET ${BASE}/config`);
  // No aplicar defaults aquí, dejar que el componente maneje los placeholders
  return {
    pageTitle: cfg?.pageTitle ?? null,
    subtitle: cfg?.subtitle ?? null,
    // estos pueden no venir si aún no migras la entidad:
    principlesVisible: !!cfg?.principlesVisible,
    ctaEnabled: !!cfg?.ctaEnabled,
    ctaLabel: cfg?.ctaLabel ?? "",
    ctaUrl: cfg?.ctaUrl ?? "",
    lastVisibleAt: cfg?.lastVisibleAt ?? null, // ← lo usamos para el badge
  };
};

/**
 * Actualiza config. Soporta:
 * - pageTitle, subtitle
 * - principlesVisible (bool)
 * - ctaEnabled (bool), ctaLabel (string), ctaUrl (string http/https o mailto:)
 * - lastVisibleAt (ISO string o null) ← para el badge “Última actualización visible”
 */
export const updateEthicsConfig = async (payload = {}) => {
  const {
    pageTitle, subtitle,
    principlesVisible,
    ctaEnabled, ctaLabel, ctaUrl,
    lastVisibleAt, // ISO o null para limpiar
  } = payload || {};

  if (ctaEnabled) {
    if (!String(ctaLabel || "").trim()) throw new Error("CTA: Debes indicar el nombre del botón.");
    if (!isValidCtaUrl(String(ctaUrl || ""))) throw new Error("CTA: La URL debe iniciar con http(s):// o mailto:");
  }

  // filtra solo las claves soportadas por el backend (evita 400 "should not exist")
  const body = {};
  if (pageTitle != null) body.pageTitle = String(pageTitle).trim();
  if (subtitle != null) body.subtitle = String(subtitle).trim();
  if (typeof principlesVisible === "boolean") body.principlesVisible = !!principlesVisible;
  if (typeof ctaEnabled === "boolean") body.ctaEnabled = !!ctaEnabled;
  if (ctaLabel != null) body.ctaLabel = String(ctaLabel).trim();
  if (ctaUrl != null) body.ctaUrl = String(ctaUrl).trim();
  if (typeof lastVisibleAt !== "undefined") body.lastVisibleAt = lastVisibleAt; // ISO o null

  const res = await fetchWithAuth(`${BASE}/config`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res, `PUT ${BASE}/config`);
};

/* ================== CONTENIDO (principios/secciones) ================== */
export const getEthicsContent = async () => {
  // 1) API
  try {
    const res = await fetchWithAuth(`${BASE}/content`, { method: "GET" });
    if (res.ok) return await res.json();
  } catch {}
  // 2) Fallback local
  try {
    const res2 = await fetch("/ethics-content.json", { cache: "no-cache" });
    if (res2.ok) return await res2.json();
  } catch {}
  return null;
};

/**
 * Solo permite { lastReview, principles, sections } para no mezclar con config.
 */
export const updateEthicsContent = async (payload = {}) => {
  const body = pick(payload, ["lastReview", "principles", "sections"]);
  const res = await fetchWithAuth(`${BASE}/content`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res, `PUT ${BASE}/content`);
};